/**
 * Analytics. Every call goes to BOTH Microsoft Clarity and GA4.
 *
 * The two are kept because they answer different questions and neither
 * subsumes the other:
 *   - Clarity gives session recordings, heatmaps and dead/rage clicks. It has
 *     no event-parameter concept at all, so anything you want to slice by has
 *     to become a session-scoped custom tag.
 *   - GA4 gives real event parameters, funnels, and the conversion signal
 *     Google Ads needs. It has no recordings.
 *
 * Two primitives, because both vendors surface them in different places:
 *   - `trackEvent` -> Clarity "event" + GA4 event. Use for things that happen.
 *   - `setTag`     -> Clarity custom tag + GA4 user property. Both are
 *     SESSION/USER-scoped, not event-scoped: a tag set at any point labels the
 *     whole session. Use for what a session *is*, not for what happened in it.
 *
 * Both are safe to call before either script has downloaded. Clarity's snippet
 * installs a synchronous queueing stub and gtag pushes onto `dataLayer`, which
 * exists from the first inline script in <head>. Both tags are deferred (gtag
 * is async, Clarity waits for idle), so that buffering is what makes early
 * calls work.
 *
 * Both tags are gated to the production hostname, so nothing below sends
 * anything from localhost or a preview build — Clarity by not loading at all,
 * GA4 by withholding the `config` call, which leaves gtag.js loaded with no
 * measurement id to send to. Either way the calls here stay silent rather
 * than throwing.
 */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 rejects an event or parameter whose name is not `[A-Za-z][A-Za-z0-9_]*`,
 * and silently truncates over-long values. Our names are hand-written and
 * already legal, but two call sites build them at runtime — `ai_referral_<id>`
 * from a referrer match, and `route_error` from an arbitrary exception message
 * — so the shape is not guaranteed by inspection. Normalising here means a bad
 * value degrades to a legible one instead of being dropped by the collector
 * with no error anywhere.
 *
 * Limits are Google's documented ones: event and parameter names 40 chars,
 * parameter values 100, user-property names 24 and values 36.
 */
function safeName(raw: string, maxLength: number): string {
  const cleaned = raw.replace(/[^A-Za-z0-9_]/g, '_').replace(/^[^A-Za-z]+/, '');
  return (cleaned || 'unnamed').slice(0, maxLength);
}

function safeValue(raw: unknown, maxLength: number): string {
  return String(raw).slice(0, maxLength);
}

/** GA4 event parameters, from the loose `Record<string, unknown>` call sites use. */
function toEventParams(params: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    out[safeName(key, 40)] =
      typeof value === 'number' || typeof value === 'boolean' ? value : safeValue(value, 100);
  }
  return out;
}

/** Record that something happened. Names should be stable and lower_snake_case. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  try {
    window.clarity?.('event', name);
  } catch {
    // Analytics must never break the page.
  }
  try {
    window.gtag?.('event', safeName(name, 40), params ? toEventParams(params) : undefined);
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * Label the session. Session-scoped in Clarity, user-scoped in GA4 — see the
 * note above.
 *
 * GA4 user properties must be registered as custom dimensions in the Admin UI
 * before they appear in reports; they are collected either way, so setting one
 * that has not been registered yet loses nothing but is invisible until it is.
 */
export function setTag(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.clarity?.('set', key, value);
  } catch {
    // Analytics must never break the page.
  }
  try {
    window.gtag?.('set', 'user_properties', {
      [safeName(key, 24)]: safeValue(value, 36),
    });
  } catch {
    // Analytics must never break the page.
  }
}

/**
 * The funnel call sites' convenience: fires the event and, for each param,
 * does the right thing per vendor.
 *
 * GA4 gets them as real EVENT parameters, which is what they are — `variant`
 * describes the form that was submitted, not the visitor. Clarity has no such
 * concept, so there they become session tags; that is a lossy fallback we
 * accept rather than a modelling choice, and it is why `variant` in Clarity
 * reflects the last form used in the session rather than each submission.
 */
export function track(name: string, params?: Record<string, unknown>): void {
  trackEvent(name, params);
  if (!params) return;
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    setTag(key, String(value));
  }
}
